import {Group} from 'src/design/atoms/Group';
import React, {useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {
  FeatureFlagEnum,
  ModuleEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {
  useCreateTaskDefinitionMutation,
  useLazyGetTaskDefinitionDetailsQuery,
} from 'src/redux/queries/taskDefinition';
import {SaveButton} from 'src/design/molecules/SaveButton';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {SidebarLinkActions} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {ActionForm, useActionForm} from 'src/app/actions/ActionForm';
import {
  ActionFormMetaRule,
  ActionsDocumentTitle,
  MapActionFormDataToTaskDefinitionRequest,
  MapActionResponseToFormData,
} from 'src/app/actions/actionHelpers';
import {TaskStateBanner} from 'src/app/tasks/TaskStateBanner';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useActionsBreadcrumbs} from '../hooks/useActionsBreadcrumbs';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IStateMachine} from 'src/types/StateMachine';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const CloneActionPage: React.FC = () => {
  useSetDocumentTitle(ActionsDocumentTitle);
  const featureFlag = FeatureFlagEnum.Actions;
  const taskType = TaskTypeEnum.Actions;
  const module = ModuleEnum.Actions;

  const {setAlert} = useAlert();
  const {taskId} = useParams();
  const [editItem, setEditItem] = useState<any>();
  const [isLoadingTask, setIsLoadingTask] = useState<boolean>(true);
  const [isSavePending, setIsSavePending] = useState<boolean>(false);

  const {backBarProps} = useActionsBreadcrumbs({
    mode: 'clone',
    item: editItem,
  });
  const {returnTo} = useBackNavigation(backBarProps.fallbackTo);

  const clonedTaskId = useRef<number | null>(null);
  const transitionUrl = `task-definition/${clonedTaskId.current}/transition`;

  const [triggerGetDetails] = useLazyGetTaskDefinitionDetailsQuery();
  const [createTaskDefinition] = useCreateTaskDefinitionMutation();

  const {
    formApi,
    formMeta,
    isLoading: isActionFormLoading,
  } = useActionForm({
    metarules: [ActionFormMetaRule.UseDueDate],
    taskState: editItem?.state,
  });
  const {resetForm} = formApi;

  useEffect(() => {
    const getData = async () => {
      if (taskId === undefined || isNaN(+taskId)) {
        return;
      }

      try {
        const response = await triggerGetDetails(+taskId).unwrap();

        const item = {
          ...MapActionResponseToFormData(response),
          internalIdentifier: undefined,
          state: TaskDefinitionStateEnum.Draft,
        };

        setEditItem(item);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
      setIsLoadingTask(false);
    };

    void getData();
  }, [taskId]);

  const submit = async () => {
    if (!(await formApi.validateForm())) {
      return {continue: false};
    }

    setIsSavePending(true);

    const {id, ...data} = formApi.methods.getValues();
    const body = MapActionFormDataToTaskDefinitionRequest(
      data,
      taskType,
      editItem.sourceFormId
    );

    try {
      const resp = await createTaskDefinition(body).unwrap();

      setAlert(`Action was successfully updated`, 'success');

      clonedTaskId.current = resp.id;
      return {
        continue: true,
        overrideTransitionBaseUrl: `task-definition/${clonedTaskId.current}/transition`,
      };
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
      setIsSavePending(false);
      return {continue: false};
    }
  };

  const onSubmitted = () =>
    returnTo(PathUtility.ActionView(String(clonedTaskId.current), 'content'));

  const handleSave = async () => {
    if (await formApi.validateForm()) {
      await submit();
      onSubmitted();
    }
  };

  useEffect(() => {
    if (formMeta.length > 0 && editItem) {
      resetForm(toFormDataV2(editItem));
    }
  }, [formMeta, editItem, resetForm]);

  const [machineBase] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });
  const machine = machineBase
    ? ({
        states: machineBase.states,
        transitions: machineBase.transitions.map(t => ({
          ...t,
          title: `Save & ${t.title}`,
        })),
      } as IStateMachine)
    : undefined;

  return (
    <ContentWrapper featureFlag={featureFlag}>
      <BackBar {...backBarProps} />
      {!isLoadingTask ? (
        <>
          <PageHeader>
            <PageHeader.Title icon={SidebarLinkActions.icon}>
              {editItem?.subject ?? 'New Action'}
            </PageHeader.Title>
          </PageHeader>

          <TaskStateBanner
            module={module}
            state={TaskDefinitionStateEnum.Draft}
          />

          <ActionForm form={formApi} isLoading={isActionFormLoading} />

          <Group align="start">
            {machine && (
              <FormTransitionBar
                preTransitionButtonNodes={[
                  <SaveButton
                    loading={isSavePending}
                    onClick={handleSave}
                    mr="md"
                  />,
                ]}
                onTransitioning={submit}
                module={module}
                stateMachine={machine}
                stateId={TaskDefinitionStateEnum.Draft}
                transitionBaseUrl={transitionUrl}
                onRefreshTransition={onSubmitted}
                bypassPermissions={true}
                invalidateTagsOnComplete={[
                  'TaskDefinitions',
                  'TaskAssignments',
                ]}
              />
            )}
          </Group>
        </>
      ) : (
        <TableBodyRender
          isLoading={isLoadingTask}
          hasData={editItem !== undefined}
        />
      )}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'form',
  path: PathUtility.ActionClone(':taskId'),
  element: <CloneActionPage />,
  isPrivate: true,
});

export default CloneActionPage;
